import { Controller } from "@hotwired/stimulus"

// Registers page tools with the browser agent (WebMCP). Chrome's preview
// exposes navigator.modelContext; the current spec moved it to
// document.modelContext. The tool list and the results come from the server.
// This file only carries calls. A drop on the booking site asks the member
// first. Public pages use GET because they have no session cookie.
// Turbo can connect the next page's controller before the old one
// disconnects, so each controller aborts only its own registration.
let active = null

export default class extends Controller {
  static targets = ["dialog", "summary", "note"]
  static values = {
    tools: Array,
    url: String,
    summary: String,
    note: String,
    lateNote: String,
    keptNote: String,
    method: { type: String, default: "post" }
  }

  connect() {
    const modelContext = navigator.modelContext || document.modelContext
    if (!modelContext) return

    active?.abort()
    this.registration = active = new AbortController()
    const { signal } = this.registration
    signal.addEventListener("abort", () => {
      this.toolsValue.forEach((tool) => modelContext.unregisterTool?.(tool.name))
    })

    this.toolsValue.forEach((tool) => {
      const { confirmOnPage, ...definition } = tool
      Promise.resolve(
        modelContext.registerTool({ ...definition, execute: (input) => this.run(tool, input) }, { signal })
      ).catch((error) => console.warn(`page tool ${tool.name}:`, error))
    })
  }

  disconnect() {
    this.registration?.abort()
    if (active === this.registration) active = null
  }

  async run(tool, input) {
    const args = this.schemaArgs(tool, input)
    let result = await this.call(tool.name, args)

    if (tool.confirmOnPage && result.requires_confirmation) {
      if (!(await this.confirmOnPage(result))) {
        return this.reply({ status: "kept", message: "The member chose to keep this class. Nothing was changed." })
      }
      const confirm = result.confirm_with || {}
      result = await this.call(tool.name, {
        ...args,
        confirmed: true,
        starts_at: confirm.starts_at,
        penalty: confirm.penalty
      })
    }

    if (["confirmed", "cancelled"].includes(result.status) && !result.already_cancelled) this.refreshPage()
    return this.reply(result)
  }

  // Only fields in the tool's schema reach the server, so an agent cannot
  // send confirmed itself and skip the page confirm.
  schemaArgs(tool, input) {
    const allowed = Object.keys(tool.inputSchema.properties)
    return Object.fromEntries(Object.entries(input || {}).filter(([key]) => allowed.includes(key)))
  }

  async call(name, input) {
    const endpoint = this.urlValue.replace(":name", name)
    const get = this.methodValue === "get"
    const response = await fetch(get ? `${endpoint}?input=${encodeURIComponent(JSON.stringify(input || {}))}` : endpoint, {
      method: get ? "GET" : "POST",
      credentials: "same-origin",
      headers: get
        ? { Accept: "application/json" }
        : {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
          },
      body: get ? undefined : JSON.stringify({ input })
    })
    return response.json().catch(() => ({ error: `The page answered ${response.status}. Nothing was changed.` }))
  }

  // The note is in the page's language. The quote's policy text is English.
  confirmOnPage(quote) {
    this.summaryTarget.textContent = this.summaryValue
      .replace("%{who}", quote.who)
      .replace("%{session}", quote.session)
      .replace("%{starts_at}", quote.starts_at)
    if (this.hasNoteTarget) {
      if (quote.penalty?.kind === "forfeit_visit") this.noteTarget.textContent = this.keptNoteValue
      else if (quote.late) this.noteTarget.textContent = this.lateNoteValue
      else this.noteTarget.textContent = this.noteValue
    }
    this.dialogTarget.returnValue = ""
    this.dialogTarget.showModal()
    return new Promise((resolve) => {
      this.dialogTarget.addEventListener("close", () => resolve(this.dialogTarget.returnValue === "drop"), { once: true })
    })
  }

  refreshPage() {
    setTimeout(() => window.Turbo?.visit(window.location.href, { action: "replace" }), 0)
  }

  reply(result) {
    return { content: [{ type: "text", text: JSON.stringify(result) }], isError: Boolean(result.error) }
  }
}
